import JSONAPIAdapter from '@ember-data/adapter/json-api';
import { service } from '@ember/service';
import * as Sentry from '@sentry/ember';
import { apiEndpoint } from 'scaleflow/utils/endpoints';
export const HOST = apiEndpoint();

// Marker the 401/403 branch of `handleResponse` hands back to `ajax`, carrying
// the JSON:API error the request would otherwise have failed with.
class RefreshAndRetry extends Error {
  constructor(adapterError) {
    super('Authentication expired; refreshing the token and retrying');
    this.name = 'RefreshAndRetry';
    this.adapterError = adapterError;
  }
}

export default class Application extends JSONAPIAdapter {
  @service currentUser;
  @service router;
  @service session;

  host = HOST;
  get headers() {
    if (!this.session.isAuthenticated) {
      this.logout();
      return {};
    }
    return {
      Authorization: this.session.data?.authenticated?.details?.accessToken,
      'Tenant-Id': this.session.activeTenantId,
    };
  }

  shouldBackgroundReloadAll() {
    return true;
  }

  shouldReloadRecord() {
    return false;
  }

  shouldReloadAll() {
    return false;
  }

  shouldBackgroundReloadRecord() {
    return true;
  }

  // Log out only when the refresh failure is terminal (the session is genuinely
  // dead — Cognito rejected the grant). Transient failures (network, throttling,
  // 5xx, timeout) keep the session and just surface the original error so the
  // request fails and recovers on a later attempt.
  handleRefreshFailure(refreshError, originalError) {
    if (refreshError?.isTerminal) {
      this.logout();
    }
    throw originalError ?? refreshError;
  }

  handleResponse(status, headers, payload, requestData) {
    if (status === 401 || status === 403) {
      // The token may have expired unexpectedly. Only *flag* that here — the
      // refresh and the retry happen in `ajax`. This hook cannot do async work:
      // RESTAdapter's fetch path `throw`s whatever it returns rather than
      // awaiting it, so returning a retry promise rejected the caller with a
      // pending Promise (`err.errors` undefined, "Error: [object Promise]") and
      // left the retry running detached, unobserved by anyone.
      return new RefreshAndRetry(
        super.handleResponse(status, headers, payload, requestData),
      );
    }
    return super.handleResponse(status, headers, payload, requestData);
  }

  // Run the request, converting the 401/403 marker back into a value so the
  // retry decision is made in one awaited place.
  async attemptAjax(url, type, options) {
    try {
      return await super.ajax(url, type, options);
    } catch (error) {
      if (error instanceof RefreshAndRetry) return error;
      throw error;
    }
  }

  async ajax(url, type, options = {}) {
    const response = await this.attemptAjax(url, type, options);
    if (!(response instanceof RefreshAndRetry)) return response;

    try {
      await this.session.refreshToken();
    } catch (refreshError) {
      // Always throws. Passing the original adapter error keeps the rejection
      // JSON:API shaped for callers that read `err?.errors?.[0]?.status`.
      this.handleRefreshFailure(refreshError, response.adapterError);
    }

    // Retry with the caller's own `options`: `requestData` only carries url and
    // method, so the old retry silently dropped the body of every write.
    const retried = await this.attemptAjax(url, type, options);
    // Still unauthorised on a fresh token means the request is genuinely
    // forbidden, not stale. Surface that instead of refreshing forever.
    if (retried instanceof RefreshAndRetry) throw retried.adapterError;
    return retried;
  }

  logout() {
    Sentry.captureMessage('Logging out due to authentication failure');
    this.router.transitionTo('logout');
  }
}
